import { BackendFilterOptions } from 'contexts/filters/backendFilterTypes'
import { getWalletAddrSourceFromDropdownValue } from 'utils/wallets/walletsUtils'

const extractDate = (isoString?: string) => {
  // we are extracting 'yyyy-mm-dd' as this is what the backend expects it to be
  const formattedDate = isoString?.match(/^(\d{4}-\d{2}-\d{2})/)
  return formattedDate ? formattedDate[0] : ''
}

export const customFieldQueryParams = ({
  select_custom_fields,
  string_custom_fields,
  number_custom_fields,
}: Pick<
  BackendFilterOptions,
  'select_custom_fields' | 'string_custom_fields' | 'number_custom_fields'
>) => {
  const params = new URLSearchParams()
  const CUSTOM_FIELD_PREFIX = 'cf'

  // Number fields: cf_<id>_min / cf_<id>_max
  for (const field of number_custom_fields) {
    const id = field.id
    const min = field.value_min
    const max = field.value_max
    if (min !== undefined && min.trim() !== '') {
      params.append(`${CUSTOM_FIELD_PREFIX}_${id}_min`, String(min))
    }
    if (max !== undefined && max.trim() !== '') {
      params.append(`${CUSTOM_FIELD_PREFIX}_${id}_max`, String(max))
    }
  }

  // String fields: repeat cf_<id>=<value> for OR logic
  for (const field of string_custom_fields) {
    const vals = Array.isArray(field.value) ? field.value : [field.value].filter(Boolean)
    for (const v of vals as Array<string | number>) {
      const value = `${v}`.trim()
      if (value) {
        params.append(`${CUSTOM_FIELD_PREFIX}_${field.id}`, value)
      }
    }
  }

  // Select fields: repeat cf_<id>=<optionId> for OR logic
  for (const field of select_custom_fields) {
    if (Array.isArray(field.value)) {
      for (const option of field.value) {
        const optionId = option.id
        if (optionId) {
          params.append(`${CUSTOM_FIELD_PREFIX}_${field.id}`, optionId)
        }
      }
    }
  }

  // Convert URLSearchParams → object while preserving repeated keys as arrays
  const result: Record<string, string | string[]> = {}
  for (const [key, value] of params.entries()) {
    if (key in result) {
      const existing = result[key]
      result[key] = Array.isArray(existing) ? [...existing, value] : [existing, value]
    } else {
      result[key] = value
    }
  }
  return result
}

export const getApiFilterParams = (currentFilters: BackendFilterOptions) => {
  const sourceValues = currentFilters.source.map((filter) => {
    // Check
    try {
      JSON.parse(filter)
      return filter
    } catch {
      // Not JSON parsable, continue with old format
    }
    // Use the old format
    return getWalletAddrSourceFromDropdownValue(filter)
  })

  return {
    page_size: currentFilters.pageSize,
    page_number: currentFilters.pageNumber,
    start: currentFilters.dateRange[0] ? extractDate(currentFilters.dateRange[0]) : '',
    end: currentFilters.dateRange[1] ? extractDate(currentFilters.dateRange[1]) : '',
    asset: currentFilters.token,
    category: currentFilters.category,
    group_transaction_type: currentFilters.type,
    source: sourceValues,
    wallet: currentFilters.wallet,
    memo: currentFilters.memo,
    to: currentFilters.to,
    from: currentFilters.from,
    txn_id: currentFilters.txn_id,
    source_type: currentFilters.source_type,
    source_tags: currentFilters.source_tags,
    asset_type: currentFilters.asset_type,
    func: currentFilters.transaction_func,
    missing_price: currentFilters.missing_price,
    ...customFieldQueryParams({
      select_custom_fields: currentFilters.select_custom_fields,
      string_custom_fields: currentFilters.string_custom_fields,
      number_custom_fields: currentFilters.number_custom_fields,
    }),
    amount_range: currentFilters.range,
    syncStatus: currentFilters.syncStatus,
    voucher_id: currentFilters.voucher,
    ...(currentFilters.version !== undefined && { version: currentFilters.version }),
  }
}
